// Přílohy: podepsané nahrání (sign-upload, PUT na uploadUrl, potvrzení), smazání.
import {
  ALLOWED_ATTACHMENT_MIMES,
  MAX_ATTACHMENT_BYTES,
  type Attachment,
  type CreateAttachmentInput,
  type SignedUpload,
  type SignUploadInput,
} from '@wac-buddy/shared'
import { api } from './client'

export type AttachmentTag = 'before' | 'after'

/** Chyba nahrání, kterou view přeloží podle `reason` (soubor nelze přijmout nebo selhal přenos). */
export class UploadError extends Error {
  constructor(readonly reason: 'type' | 'size' | 'transfer') {
    super(reason)
    this.name = 'UploadError'
  }
}

type AllowedMime = (typeof ALLOWED_ATTACHMENT_MIMES)[number]

export function isAllowedMime(mime: string): mime is AllowedMime {
  return (ALLOWED_ATTACHMENT_MIMES as readonly string[]).includes(mime)
}

export function signUpload(input: SignUploadInput): Promise<SignedUpload> {
  return api('/attachments/sign-upload', { method: 'POST', body: JSON.stringify(input) })
}

export function createAttachment(input: CreateAttachmentInput): Promise<Attachment> {
  return api('/attachments', { method: 'POST', body: JSON.stringify(input) })
}

export function deleteAttachment(id: string): Promise<void> {
  return api(`/attachments/${encodeURIComponent(id)}`, { method: 'DELETE' })
}

export interface UploadJobPhotoOptions {
  jobId: string
  file: File
  tag: AttachmentTag
  marketingConsent: boolean
}

/** Nahraje fotku k zakázce: podpis, PUT souboru (bez cookies), zápis přílohy. */
export async function uploadJobPhoto(options: UploadJobPhotoOptions): Promise<Attachment> {
  const { jobId, file, tag, marketingConsent } = options
  if (!isAllowedMime(file.type)) throw new UploadError('type')
  if (file.size < 1 || file.size > MAX_ATTACHMENT_BYTES) throw new UploadError('size')

  const name = file.name || `foto-${tag}.jpg`
  const signed = await signUpload({
    entityType: 'job',
    entityId: jobId,
    name,
    mime: file.type,
    size: file.size,
  })

  let res: Response
  try {
    res = await fetch(signed.uploadUrl, {
      method: signed.method,
      headers: signed.headers,
      body: file,
    })
  } catch {
    throw new UploadError('transfer')
  }
  if (!res.ok) throw new UploadError('transfer')

  return createAttachment({
    entityType: 'job',
    entityId: jobId,
    key: signed.key,
    name,
    mime: file.type,
    size: file.size,
    kind: 'photo',
    tags: [tag],
    marketingConsent,
  })
}

/**
 * Nahraje soubor k firmě (logo): podpis, PUT souboru, zápis přílohy entity `org`.
 * Vrací klíč v úložišti, ten se ukládá do brand kitu.
 */
export async function uploadOrgFile(orgId: string, file: File): Promise<string> {
  if (!isAllowedMime(file.type)) throw new UploadError('type')
  if (file.size < 1 || file.size > MAX_ATTACHMENT_BYTES) throw new UploadError('size')

  const name = file.name || 'logo'
  const signed = await signUpload({
    entityType: 'org',
    entityId: orgId,
    name,
    mime: file.type,
    size: file.size,
  })

  let res: Response
  try {
    res = await fetch(signed.uploadUrl, {
      method: signed.method,
      headers: signed.headers,
      body: file,
    })
  } catch {
    throw new UploadError('transfer')
  }
  if (!res.ok) throw new UploadError('transfer')

  await createAttachment({
    entityType: 'org',
    entityId: orgId,
    key: signed.key,
    name,
    mime: file.type,
    size: file.size,
    kind: file.type.startsWith('image/') ? 'photo' : 'document',
    tags: [],
    marketingConsent: false,
  })
  return signed.key
}
